import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, LayoutGrid, Image as ImageIcon, Inbox, KeyRound, ArrowUpRight, Users as UsersIcon } from "lucide-react";

const NAV_BASE = [
  { to: "/admin", label: "Overview", icon: LayoutGrid, end: true, testid: "admin-nav-overview" },
  { to: "/admin/gallery", label: "Gallery", icon: ImageIcon, testid: "admin-nav-gallery" },
  { to: "/admin/inbox", label: "Inbox", icon: Inbox, testid: "admin-nav-inbox" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const NAV = [...NAV_BASE];
  if (user?.role === "admin") {
    NAV.push({ to: "/admin/users", label: "Users", icon: UsersIcon, testid: "admin-nav-users" });
  }
  NAV.push({ to: "/admin/account", label: "Account", icon: KeyRound, testid: "admin-nav-account" });

  const onLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen flex bg-[#0d0d0d] text-white">
      <aside
        data-testid="admin-sidebar"
        className="hidden lg:flex flex-col fixed top-0 left-0 h-screen w-[240px] bg-[#070707] border-r border-[#1a1a1a] z-30"
      >
        <div className="px-6 pt-8 pb-6">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C]">
            HighwaysLens
          </div>
          <div className="font-display text-xl tracking-tight mt-1">Studio Admin</div>
        </div>
        <nav className="flex flex-col gap-1 px-2">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) => `hl-sidebar-link ${isActive ? "active" : ""}`}
            >
              <item.icon size={14} className="opacity-80" />
              <span className="font-display tracking-tight">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto px-4 pb-6">
          <NavLink
            to="/"
            target="_blank"
            data-testid="admin-view-public"
            className="text-[11px] tracking-[0.24em] uppercase text-[#A3A3A3] hover:text-white flex items-center gap-2 px-3 py-2 mb-2"
          >
            View site <ArrowUpRight size={12} />
          </NavLink>
          <div className="px-3 mb-3">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#525252]">Signed in</div>
            <div className="text-[12px] text-[#c9d1d9] truncate" data-testid="admin-user-email">
              {user?.email}
            </div>
            {user?.role && (
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#658CAE] mt-0.5">
                {user.role}
              </div>
            )}
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#A3A3A3] hover:text-white px-3 py-2 border-t border-[#1a1a1a]"
          >
            <LogOut size={14} /> Log out
          </button>
        </div>
      </aside>

      <main className="flex-1 lg:ml-[240px] min-h-screen">
        <header className="lg:hidden flex items-center justify-between border-b border-[#1a1a1a] px-4 py-3 bg-[#070707] sticky top-0 z-20">
          <div className="font-display text-base tracking-tight">Studio Admin</div>
          <button onClick={onLogout} className="text-[11px] tracking-[0.24em] uppercase text-[#A3A3A3]">
            <LogOut size={14} className="inline mr-1" /> Log out
          </button>
        </header>
        <div className="lg:hidden flex gap-1 overflow-x-auto border-b border-[#1a1a1a] bg-[#070707]">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `px-4 py-3 text-[11px] tracking-[0.24em] uppercase whitespace-nowrap ${isActive ? "text-white border-b-2 border-[#4A6D8C]" : "text-[#737373]"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
        <Outlet />
      </main>
    </div>
  );
}
